import GenericTable from "@/components/GenericTable";
import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  Heading,
  Text,
} from "@chakra-ui/react";
import { useMemo } from "react";
import { formatCalendarDate } from "../../contracts/columns";
import type { IntegralCoverage } from "../integralCoverage";
import Kpi from "../components/Kpi";
import StepFooter from "../components/StepFooter";
import {
  enrollmentStatusLabel,
  formatCop,
  formatRate,
  rateFor,
  vatLabel,
} from "../format";
import type { BenchInput, QuoteRow } from "../types";
import { useInductionPreApproved } from "../useInductionPreApproved";
import { quoteRowFor } from "../quoteRow";
import { useInductionQuote } from "../useInductionQuote";
import SummaryRow from "./SummaryRow";

const COLUMNS = [
  { key: "document", label: "Cédula", minWidth: 150 },
  { key: "tenant", label: "Inquilino", minWidth: 180 },
  { key: "address", label: "Inmueble", minWidth: 220 },
  { key: "rent", label: "Canon", minWidth: 130 },
  { key: "integralCoverage", label: "Amparo integral", minWidth: 150 },
] as const;

type Props = {
  bench: BenchInput;
  withVat: boolean;
  selected: ReadonlySet<string>;
  // Same for every contract: the highest declared in Contratos actuales.
  integralCoverage: IntegralCoverage;
  accuracy: boolean;
  onAccuracy: (accepted: boolean) => void;
  isSubmitting: boolean;
  // The last attempt got no answer we can trust: the batch may exist.
  isUnknown: boolean;
  onBack: () => void;
  // Carries the numbers on screen: the server compares them with its own.
  onSubmit: (
    quoted: Pick<QuoteRow, "pct" | "rateWithVat" | "monthlyCost">
  ) => void;
};

const SummaryStep = ({
  bench,
  withVat,
  selected,
  integralCoverage,
  accuracy,
  onAccuracy,
  isSubmitting,
  isUnknown,
  onBack,
  onSubmit,
}: Props): JSX.Element => {
  const { screenings, isLoading } = useInductionPreApproved();
  const selectedIds = useMemo(() => [...selected], [selected]);
  // Same bench as Selección, so the quote it cached is the one found here.
  const { quote, isFetching, isError, refetch, isCurrent } = useInductionQuote({
    bench,
  });
  const row = useMemo(
    () =>
      quoteRowFor(
        quote,
        screenings?.map((screening) => screening.screeningId) ?? [],
        selected
      ),
    [quote, screenings, selected]
  );

  const rows = useMemo(
    () =>
      screenings?.filter((screening) => selected.has(screening.screeningId)),
    [screenings, selected]
  );
  // Display only: the server prices from the ids.
  const rentTotal = useMemo(
    () => rows?.reduce((sum, screening) => sum + screening.monthlyRent, 0),
    [rows]
  );

  // The numbers on screen are the ones `submit` will carry, so wait for them
  // to settle for this exact selection.
  const ready = !!row && isCurrent && !isFetching;
  const dimmed = !ready;

  return (
    <Card.Root borderRadius="lg" border="none">
      <Card.Body p="5" display="flex" flexDirection="column" gap="4">
        <Box>
          <Heading size="xl">Revisa tu inducción</Heading>
          <Text color="gray.600">
            Estos son los números que quedan registrados. A todos los contratos
            les aplicamos el amparo integral más alto que indicaste en tus
            contratos actuales.
          </Text>
        </Box>
        <Box
          display="grid"
          gridTemplateColumns={{
            base: "1fr",
            sm: "1fr 1fr",
            lg: "repeat(5, 1fr)",
          }}
          gap="3"
        >
          <Kpi
            label="Monto a afianzar"
            value={rentTotal === undefined ? "—" : formatCop(rentTotal)}
          />
          <Kpi
            label={`Tasa (${vatLabel(withVat)})`}
            value={row ? formatRate(rateFor(row, withVat)) : "—"}
            dimmed={dimmed}
          />
          <Kpi
            label="Valor a pagar / mes"
            value={row ? formatCop(row.monthlyCost) : "—"}
            dimmed={dimmed}
          />
          <Kpi label="Contratos" value={String(selectedIds.length)} />
          <Kpi
            label="Estado según fecha"
            value={quote ? enrollmentStatusLabel(quote.enrollmentStatus) : "—"}
            note={
              quote
                ? `Desde ${formatCalendarDate(quote.effectiveDate)}`
                : undefined
            }
            dimmed={dimmed}
          />
        </Box>
        {isError && !quote && (
          <Alert.Root status="error" colorPalette="red">
            <Alert.Indicator />
            <Alert.Content>
              <Alert.Description>
                No pudimos calcular los valores de esta selección.
              </Alert.Description>
            </Alert.Content>
            <Button size="sm" variant="outline" onClick={() => refetch()}>
              Reintentar
            </Button>
          </Alert.Root>
        )}
        <Checkbox.Root
          checked={accuracy}
          onCheckedChange={(e) => onAccuracy(e.checked === true)}
          alignItems="flex-start"
        >
          <Checkbox.HiddenInput />
          <Checkbox.Control />
          <Checkbox.Label>
            Acredito la información: Declaro que todos los datos registrados son
            verídicos y que el amparo integral de cada contrato corresponde al
            que tiene actualmente.
          </Checkbox.Label>
        </Checkbox.Root>
        <GenericTable isLoading={isLoading} columns={COLUMNS} size="sm">
          {rows?.map((screening) => (
            <SummaryRow
              key={screening.screeningId}
              screening={screening}
              integralCoverage={integralCoverage}
            />
          ))}
        </GenericTable>
        {isUnknown && (
          <Alert.Root status="warning" colorPalette="orange">
            <Alert.Indicator />
            <Alert.Content>
              <Alert.Description>
                No pudimos confirmar el envío de tu inducción. Intenta de nuevo;
                si ya había llegado, no pasa nada.
              </Alert.Description>
            </Alert.Content>
          </Alert.Root>
        )}
        <StepFooter onBack={onBack}>
          <Button
            loading={isSubmitting}
            disabled={!accuracy || !ready}
            onClick={() =>
              row &&
              onSubmit({
                pct: row.pct,
                rateWithVat: row.rateWithVat,
                monthlyCost: row.monthlyCost,
              })
            }
          >
            {isUnknown ? "Reintentar" : "Finalizar inducción"}
          </Button>
        </StepFooter>
      </Card.Body>
    </Card.Root>
  );
};

export default SummaryStep;
